import type {
  GamepadState,
  InputSnapshot,
  KeyboardState,
  MouseState,
  TouchState,
} from "./types.js";

/**
 * Standalone input sampler. Keyboard on window (reliable focus),
 * pointer/touch on the stage container.
 */
export class InputManager {
  private keyboard: KeyboardState = {
    keys: new Set(),
    justPressed: new Set(),
    justReleased: new Set(),
  };
  private mouse: MouseState = {
    x: 0,
    y: 0,
    deltaX: 0,
    deltaY: 0,
    buttons: 0,
    justClicked: false,
  };
  private touch: TouchState = { touches: [], justTapped: false };
  private gamepad: GamepadState = {
    connected: false,
    axes: [],
    buttons: [],
    justPressed: [],
  };
  private paused = false;
  private readonly cleanups: Array<() => void> = [];

  constructor(private readonly container: HTMLElement) {
    this.attach();
  }

  private attach(): void {
    this.container.tabIndex = this.container.tabIndex >= 0 ? this.container.tabIndex : 0;

    const setMousePosition = (e: MouseEvent) => {
      const rect = this.container.getBoundingClientRect();
      this.mouse.x = e.clientX - rect.left;
      this.mouse.y = e.clientY - rect.top;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (this.paused) return;
      // keep page from scrolling on Space / arrows during play
      if (
        e.code === "Space" ||
        e.code === "ArrowUp" ||
        e.code === "ArrowDown" ||
        e.code === "ArrowLeft" ||
        e.code === "ArrowRight"
      ) {
        e.preventDefault();
      }
      this.keyboard.keys.add(e.code);
      this.keyboard.justPressed.add(e.code);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      this.keyboard.keys.delete(e.code);
      if (!this.paused) this.keyboard.justReleased.add(e.code);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (this.paused) return;
      setMousePosition(e);
      this.mouse.deltaX = e.movementX;
      this.mouse.deltaY = e.movementY;
    };
    const onMouseDown = (e: MouseEvent) => {
      if (this.paused) return;
      setMousePosition(e);
      this.mouse.buttons = e.buttons;
      this.mouse.justClicked = true;
      this.container.focus();
    };
    const onMouseUp = (e: MouseEvent) => {
      this.mouse.buttons = e.buttons;
    };
    const onTouchStart = (e: TouchEvent) => {
      if (this.paused) return;
      this.updateTouches(e);
      this.touch.justTapped = true;
      this.container.focus();
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!this.paused) this.updateTouches(e);
    };
    const onTouchEnd = (e: TouchEvent) => this.updateTouches(e);
    const onBlur = () => {
      this.keyboard.keys.clear();
      this.keyboard.justPressed.clear();
      this.keyboard.justReleased.clear();
      this.mouse.buttons = 0;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    this.container.addEventListener("mousemove", onMouseMove);
    this.container.addEventListener("mousedown", onMouseDown);
    this.container.addEventListener("mouseup", onMouseUp);
    this.container.addEventListener("touchstart", onTouchStart, { passive: true });
    this.container.addEventListener("touchmove", onTouchMove, { passive: true });
    this.container.addEventListener("touchend", onTouchEnd, { passive: true });

    this.cleanups.push(
      () => window.removeEventListener("keydown", onKeyDown),
      () => window.removeEventListener("keyup", onKeyUp),
      () => window.removeEventListener("blur", onBlur),
      () => this.container.removeEventListener("mousemove", onMouseMove),
      () => this.container.removeEventListener("mousedown", onMouseDown),
      () => this.container.removeEventListener("mouseup", onMouseUp),
      () => this.container.removeEventListener("touchstart", onTouchStart),
      () => this.container.removeEventListener("touchmove", onTouchMove),
      () => this.container.removeEventListener("touchend", onTouchEnd),
    );
  }

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    this.paused = false;
    this.keyboard.keys.clear();
    this.keyboard.justPressed.clear();
    this.keyboard.justReleased.clear();
  }

  private updateTouches(e: TouchEvent): void {
    const rect = this.container.getBoundingClientRect();
    this.touch.touches = Array.from(e.touches).map((t) => ({
      id: t.identifier,
      x: t.clientX - rect.left,
      y: t.clientY - rect.top,
      startX: t.clientX - rect.left,
      startY: t.clientY - rect.top,
    }));
  }

  snapshot(): InputSnapshot {
    if (this.paused) {
      return {
        keyboard: {
          keys: new Set(),
          justPressed: new Set(),
          justReleased: new Set(),
        },
        mouse: { ...this.mouse, buttons: 0, justClicked: false },
        touch: { touches: [], justTapped: false },
        gamepad: { connected: false, axes: [], buttons: [], justPressed: [] },
      };
    }
    this.pollGamepad();
    const snap: InputSnapshot = {
      keyboard: {
        keys: new Set(this.keyboard.keys),
        justPressed: new Set(this.keyboard.justPressed),
        justReleased: new Set(this.keyboard.justReleased),
      },
      mouse: { ...this.mouse },
      touch: {
        touches: [...this.touch.touches],
        justTapped: this.touch.justTapped,
      },
      gamepad: {
        ...this.gamepad,
        axes: [...this.gamepad.axes],
        buttons: [...this.gamepad.buttons],
        justPressed: [...this.gamepad.justPressed],
      },
    };
    this.keyboard.justPressed.clear();
    this.keyboard.justReleased.clear();
    this.mouse.deltaX = 0;
    this.mouse.deltaY = 0;
    this.mouse.justClicked = false;
    this.touch.justTapped = false;
    return snap;
  }

  private pollGamepad(): void {
    const gp = Array.from(navigator.getGamepads?.() ?? []).find((g) => g?.connected);
    if (!gp) {
      this.gamepad.connected = false;
      return;
    }
    const prevButtons = this.gamepad.buttons;
    this.gamepad.connected = true;
    this.gamepad.axes = gp.axes.map((a) => a);
    this.gamepad.buttons = gp.buttons.map((b) => b.pressed);
    this.gamepad.justPressed = gp.buttons.map((b, i) => b.pressed && !prevButtons[i]);
  }

  destroy(): void {
    for (const c of this.cleanups) c();
    this.cleanups.length = 0;
    this.resume();
  }
}
